import { z } from 'zod';
import type {
    LegalDocumentAcceptancesSearchRequest,
    OutstandingSignature,
    OutstandingSignatureState,
    OutstandingSignaturesSearchRequest,
    SearchOutstandingSignaturesPageResponse,
} from '../legal-reporting.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { AuthorizationPaginationLimitQuerySchema } from './authorization.schemas';
import { NonEmptyStringSchema } from './common.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';
import { SystemDisplayTimeZoneSchema } from '../timezone.utils';
import {
    LegalDocumentListItemSchema,
    LegalDocumentsOpaqueCursorQuerySchema,
} from './legal-documents.schemas';

export const OutstandingSignatureStateSchema: z.ZodType<OutstandingSignatureState> = z.enum([
    'unsigned',
    'expired',
    'expiring_soon',
]);

export const OutstandingSignaturesSearchRequestSchema: z.ZodType<OutstandingSignaturesSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: LegalDocumentsOpaqueCursorQuerySchema,
            signature_states: z.array(OutstandingSignatureStateSchema).min(1).max(3).optional(),
        })
        .strict();

const outstandingSignatureBaseSchema = z.object({
    legal_document_assignment_uuid: z.string().uuid(),
    organization: z
        .object({
            organization_uuid: z.string().uuid(),
            organization_name: NonEmptyStringSchema,
        })
        .strict(),
    legal_document: LegalDocumentListItemSchema,
});

const outstandingSignatureLatestSignatureSchema = z
    .object({
        accepted_ts: z.string().datetime(),
        expires_ts: z.string().datetime(),
        signer: z
            .object({ user_uuid: z.string().uuid(), primary_email: NonEmptyStringSchema })
            .strict(),
    })
    .strict();

export const OutstandingSignatureSchema: z.ZodType<OutstandingSignature> = z.discriminatedUnion(
    'signature_state',
    [
        outstandingSignatureBaseSchema
            .extend({ signature_state: z.literal('unsigned'), latest_signature: z.null() })
            .strict(),
        outstandingSignatureBaseSchema
            .extend({
                signature_state: z.literal('expired'),
                latest_signature: outstandingSignatureLatestSignatureSchema,
            })
            .strict(),
        outstandingSignatureBaseSchema
            .extend({
                signature_state: z.literal('expiring_soon'),
                latest_signature: outstandingSignatureLatestSignatureSchema,
            })
            .strict(),
    ]
);

export const SearchOutstandingSignaturesPageResponseSchema: z.ZodType<SearchOutstandingSignaturesPageResponse> =
    z
        .object({
            outstanding_signatures: z.array(OutstandingSignatureSchema),
            pagination: OpaqueCursorPaginationSchema,
            display_timezone: SystemDisplayTimeZoneSchema,
        })
        .strict();

export const LegalDocumentAcceptancesSearchRequestSchema: z.ZodType<LegalDocumentAcceptancesSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: LegalDocumentsOpaqueCursorQuerySchema,
            organization_uuid: z.string().uuid().optional(),
            accepted_text: z.enum(['current', 'earlier']).optional(),
        })
        .strict();
